/**
 * Routing Orchestrator
 * 
 * The SINGLE authoritative entry point for routing any lead in the system.
 * This layer coordinates and sequences existing modules without introducing
 * new business logic.
 * 
 * ORCHESTRATION FLOW (STRICT ORDER):
 * 1. Normalize & validate lead input
 * 2. Fetch active context (contracts, delivery targets, ledger state)
 * 3. Run Agent Pool Selection (with goal-aware pacing & guardrails)
 * 4. Run Auction / Allocation (multi-agent routing engine)
 * 5. Attempt Delivery (mark routed or unsold)
 * 6. Retry Decision (enqueue retry or stock lead)
 * 7. Return Final Routing Result with observability metadata
 * 
 * This module is pure coordination logic - no UI dependencies.
 */

import {
  selectAgentPool,
  type PoolMeta,
  type AgentWithPacing,
  type AgentPacingScore,
} from './agentPoolSelector';
import {
  routeLeadMultiAgent,
  type IncomingLead,
  type EligibilityContext,
  type AgentPickAttempt,
} from './multiAgentRouter';
import {
  createLifecycleLead,
  startRouting,
  markRouted,
  markUnsold,
  queueRetry,
  startRetryRouting,
  stockLead,
  type LifecycleLead,
  type LeadStatus,
  getRetryDelay,
} from './leadStateMachine';
import type { AllocationBreakdown, PricingRuleForRouting } from './leadRouter';
import type { MockAgent } from '@/contexts/MockAgentsContext';
import type { AllocationWeights } from '@/contexts/AllocationWeightsContext';
import type { AgentContractPressure } from '@/contexts/ContractsContext';
import type { IncomingLeadPayload } from './leads/leadTypes';
import { normalizeLeadProfile } from './leads/normalizeLeadProfile';
import type { GuardrailInputs, GuardrailSummary } from './executionGuardrails';
import type { ControlSignals } from './deliveryControl';

// Import event recording
import { recordRoutingEvent, buildRoutingEvent, type RoutingEventSource } from './routingEvents';

// Import routing attempt recording
import { buildRoutingAttempt } from './leads/routingAttemptBuilder';
import { recordRoutingAttemptToDb } from '@/hooks/useRoutingAttempts';

// Import lead persistence
import { upsertLeadToDb } from './leads/persistLead';

// Import DEV overrides
import { applyDevOverridesToPool, areDevOverridesActive, DEV_AGENT_OVERRIDES } from './dev/agentOverrides';
import {
  applyPerAgentOverridesToPool,
  arePerAgentOverridesActive,
  capturePerAgentOverridesSnapshot,
} from './dev/perAgentOverrides';
import {
  resolveEligibleAgentsWithDevInjection,
  isDevInjectionAvailable,
  type InjectionMetadata,
} from './dev/eligibilityInjection';
import type { RoutingContextSnapshot } from './leadStateMachine';

// --- Input Types ---

export type IngestionSource = 'simulation' | 'retry' | 'real';

export interface LeadInput {
  // Core lead data
  lead: {
    id: string;
    leadTypeId: string;
    leadTypeName: string;
    vertical: 'life_insurance' | 'mortgage';
    state: string;
    estimatedValue: number;
  };
  // Raw payload (for persistence + profile normalization)
  sourcePayload?: IncomingLeadPayload;
  // Ingestion source
  source: IngestionSource;
  // Optional flags
  dryRun?: boolean;
  debug?: boolean;
  // For retry routing, pass the existing lifecycle lead
  existingLead?: LifecycleLead;
  // Simulation metadata
  simulationRunId?: string;
  virtualTime?: number;
}

export interface RoutingContext {
  // Required
  agents: MockAgent[] | AgentWithPacing[];
  weights: AllocationWeights;
  // Optional eligibility context
  getPricingRule?: (leadTypeId: string, state: string) => PricingRuleForRouting | null;
  checkIntake?: (vertical: 'life_insurance' | 'mortgage') => string | null;
  // Optional pacing & pressure data
  agentPacingScores?: Map<string, AgentPacingScore>;
  contractPressures?: Map<string, AgentContractPressure>;
  // Optional control signals (from Delivery Control Plane)
  controlSignals?: ControlSignals;
  agentGuardrails?: Map<string, GuardrailInputs>;
  // Pool selection config
  targetPoolSize?: number;
  usePoolSelection?: boolean;
  // Contract-level lead type restrictions: agentId → arrays of lead_types from each active contract
  agentContractLeadTypes?: Map<string, string[][]>;
}

// --- Output Types ---

export type RoutingOutcome = 'ROUTED' | 'UNSOLD' | 'RETRY_QUEUED' | 'STOCKED' | 'VALIDATION_FAILED';

export interface DeliveryImpact {
  agentId?: string;
  agentName?: string;
  chargedAmount?: number;
  contractId?: string;
  willCountTowardTarget: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface RoutingObservability {
  // Timing
  startedAt: number;
  completedAt: number;
  durationMs: number;
  // Pool selection
  poolMeta?: PoolMeta;
  poolSize: number;
  // Auction results
  eligibleCount: number;
  ineligibleCount: number;
  totalAgentsEvaluated: number;
  priorityScore?: number;
  allocationBreakdown?: AllocationBreakdown;
  // Pick attempts
  pickAttempts: AgentPickAttempt[];
  evaluatedAgents: Array<{
    agentId: string;
    agentName: string;
    eligible: boolean;
    effectiveBid?: number;
    priorityScore?: number;
    breakdown?: AllocationBreakdown;
    rejectionCode?: string;
    rejectionReason?: string;
  }>;
  // Guardrails
  guardrailSummary?: GuardrailSummary;
  guardrailsApplied: boolean;
  // Delivery pressure
  pressureBoostApplied: boolean;
  pressureBoostValue?: number;
  // DEV eligibility injection
  eligibilitySource: 'REAL' | 'DEV_INJECTED';
  devInjectedAgentIds?: string[];
  // Retry decision
  retryDecision?: {
    willRetry: boolean;
    retryCount: number;
    maxRetries: number;
    nextRetryAt?: number;
    reason: string;
  };
  // Rejection info (if unsold)
  rejectionReason?: string;
  rejectionCode?: string;
}

export interface RoutingResult {
  // Outcome
  outcome: RoutingOutcome;
  success: boolean;
  // Lead state
  lead: LifecycleLead;
  previousStatus?: LeadStatus;
  // Delivery impact
  deliveryImpact: DeliveryImpact;
  // Observability
  observability: RoutingObservability;
  // Validation errors (if validation failed)
  validationErrors?: ValidationError[];
  // Dry run flag
  isDryRun: boolean;
  // Persistence promise (optional, for admin flows that need to await DB writes)
  persistence?: Promise<void>;
}

// --- Deterministic Random Helper ---

function hashStringToSeed(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createDeterministicRandom(seedString: string): () => number {
  let seed = hashStringToSeed(seedString) || 1;
  return () => {
    // Mulberry32 PRNG
    seed += 0x6D2B79F5;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- Validation ---

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  normalizedLead?: IncomingLead;
}

function validateAndNormalizeInput(input: LeadInput): ValidationResult {
  const errors: ValidationError[] = [];
  const { lead } = input;

  // Required field validation
  if (!lead.id || typeof lead.id !== 'string') {
    errors.push({ field: 'lead.id', message: 'Lead ID is required and must be a string' });
  }

  if (!lead.leadTypeId || typeof lead.leadTypeId !== 'string') {
    errors.push({ field: 'lead.leadTypeId', message: 'Lead type ID is required' });
  }

  if (!lead.leadTypeName || typeof lead.leadTypeName !== 'string') {
    errors.push({ field: 'lead.leadTypeName', message: 'Lead type name is required' });
  }

  if (!lead.vertical || !['life_insurance', 'mortgage'].includes(lead.vertical)) {
    errors.push({ field: 'lead.vertical', message: 'Vertical must be "life_insurance" or "mortgage"' });
  }

  if (!lead.state || typeof lead.state !== 'string' || lead.state.length !== 2) {
    errors.push({ field: 'lead.state', message: 'State must be a 2-letter code' });
  }

  if (typeof lead.estimatedValue !== 'number' || lead.estimatedValue <= 0) {
    errors.push({ field: 'lead.estimatedValue', message: 'Estimated value must be a positive number' });
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // Normalize: trim strings, uppercase state
  const normalizedLead: IncomingLead = {
    id: lead.id.trim(),
    leadTypeId: lead.leadTypeId.trim(),
    leadTypeName: lead.leadTypeName.trim(),
    vertical: lead.vertical,
    state: lead.state.toUpperCase().trim(),
    estimatedValue: lead.estimatedValue,
  };

  return { valid: true, errors: [], normalizedLead };
}

// --- Context Building ---

function buildEligibilityContext(
  context: RoutingContext,
  agentPacingScores?: Map<string, AgentPacingScore>,
  agentsIgnoringCaps?: Set<string>,
  agentsIgnoringPricing?: Set<string>
): EligibilityContext {
  return {
    getPricingRule: context.getPricingRule,
    checkIntake: context.checkIntake,
    agentPacingScores: agentPacingScores ?? context.agentPacingScores,
    agentGuardrails: context.agentGuardrails,
    agentsIgnoringCaps,
    agentsIgnoringPricing,
    agentContractLeadTypes: context.agentContractLeadTypes,
  };
}

// --- Main Orchestrator Function ---

/**
 * Route an incoming lead through the complete orchestration flow.
 * 
 * This is the ONLY valid entry point for routing any lead in the system.
 */
export function routeIncomingLead(
  input: LeadInput,
  context: RoutingContext
): RoutingResult {
  const startedAt = Date.now();
  const { source, dryRun = false, debug = false, existingLead, sourcePayload } = input;

  // Log if debug mode
  if (debug) {
    console.log('[Orchestrator] Starting routing for lead:', input.lead.id);
    console.log('[Orchestrator] Source:', source);
    console.log('[Orchestrator] Dry run:', dryRun);
  }

  // --- Step 1: Normalize & Validate ---
  const validation = validateAndNormalizeInput(input);
  
  if (!validation.valid || !validation.normalizedLead) {
    const now = Date.now();
    
    // Create a minimal lead for error response
    const errorLead: LifecycleLead = existingLead ?? {
      id: input.lead.id || 'unknown',
      leadTypeId: input.lead.leadTypeId || 'unknown',
      leadTypeName: input.lead.leadTypeName || 'Unknown',
      vertical: input.lead.vertical || 'life_insurance',
      state: input.lead.state || 'XX',
      estimatedValue: input.lead.estimatedValue || 0,
      status: 'NEW',
      retryCount: 0,
      maxRetries: 2,
      createdAt: now,
      lastUpdatedAt: now,
    };

    return {
      outcome: 'VALIDATION_FAILED',
      success: false,
      lead: errorLead,
      deliveryImpact: { willCountTowardTarget: false },
      observability: {
        startedAt,
        completedAt: now,
        durationMs: now - startedAt,
        poolSize: 0,
        eligibleCount: 0,
        ineligibleCount: 0,
        totalAgentsEvaluated: 0,
        pickAttempts: [],
        evaluatedAgents: [],
        guardrailsApplied: false,
        pressureBoostApplied: false,
        eligibilitySource: 'REAL',
      },
      validationErrors: validation.errors,
      isDryRun: dryRun,
    };
  }

  const normalizedLead = validation.normalizedLead;

  // --- Step 2: Initialize or Update Lead Lifecycle ---
  let lifecycleLead: LifecycleLead;
  let previousStatus: LeadStatus | undefined;
  let attemptNumber: number;
  
  // Capture current DEV overrides state for routing context
  const currentDevOverridesActive = source !== 'simulation' && areDevOverridesActive();
  const currentPerAgentOverridesActive = source !== 'simulation' && arePerAgentOverridesActive();
  
  const captureRoutingContext = (): RoutingContextSnapshot | undefined => {
    if (!currentDevOverridesActive && !currentPerAgentOverridesActive) return undefined;
    
    const perAgentSnapshot = currentPerAgentOverridesActive 
      ? capturePerAgentOverridesSnapshot() 
      : undefined;
    
    return {
      devOverridesEnabled: currentDevOverridesActive || currentPerAgentOverridesActive,
      forceAllAgentsEligible: currentDevOverridesActive && DEV_AGENT_OVERRIDES.forceAllAgentsEligible ? true : undefined,
      forceWalletBalance: currentDevOverridesActive ? DEV_AGENT_OVERRIDES.forceWalletBalance : undefined,
      forceLicenses: currentDevOverridesActive && DEV_AGENT_OVERRIDES.forceLicenses && DEV_AGENT_OVERRIDES.forceLicenses.length > 0 
        ? [...DEV_AGENT_OVERRIDES.forceLicenses] 
        : undefined,
      perAgentOverrides: perAgentSnapshot,
      capturedAt: Date.now(),
      source: source === 'retry' ? 'retry' : 'lab',
    };
  };
  
  // CRITICAL FIX: Retries should NEVER use persisted routing context
  // They must re-evaluate DEV overrides from current session state
  // This ensures DEV overrides enabled after initial failure take effect
  let usePersistedDevContext = false;
  let _usePersistedPerAgentContext = false;
  
  // Only use persisted context for review-approved leads (not retries)
  // Retries always use fresh session state to allow DEV override changes
  if (source !== 'retry' && source !== 'simulation' && existingLead?.routingContext?.devOverridesEnabled) {
    usePersistedDevContext = true;
    _usePersistedPerAgentContext = !!existingLead.routingContext.perAgentOverrides;
    if (debug) {
      console.log('[Orchestrator] Using persisted DEV routing context from lead (non-retry)');
    }
  }
  
  if (debug && source === 'retry') {
    console.log('[Orchestrator] Retry path: forcing fresh DEV override evaluation');
    console.log('[Orchestrator] Current session DEV overrides active:', currentDevOverridesActive);
    console.log('[Orchestrator] Current session per-agent overrides active:', currentPerAgentOverridesActive);
  }

  if (source === 'retry' && existingLead) {
    // Retry routing: transition from RETRY_QUEUED to ROUTING_IN_PROGRESS
    previousStatus = existingLead.status;
    attemptNumber = existingLead.retryCount + 1; // Capture before transition
    lifecycleLead = startRetryRouting(existingLead, existingLead.pricingSnapshot);
    
    // ALWAYS capture fresh routing context for retries from current session state
    // This ensures DEV overrides enabled after the initial failure are respected
    lifecycleLead = { 
      ...lifecycleLead, 
      routingContext: captureRoutingContext() 
    };
    
    if (debug) {
      console.log('[Orchestrator] Retry: captured fresh routing context:', lifecycleLead.routingContext);
    }
  } else if (existingLead) {
    // Existing lead provided (e.g., re-routing attempt from review)
    previousStatus = existingLead.status;
    attemptNumber = existingLead.retryCount + 1;
    lifecycleLead = startRouting(existingLead);
    // Capture routing context for review-approved leads
    lifecycleLead = { 
      ...lifecycleLead, 
      routingContext: captureRoutingContext() 
    };
  } else {
    // New lead: create lifecycle lead and start routing
    const leadProfile = sourcePayload ? normalizeLeadProfile(sourcePayload, normalizedLead.leadTypeId) : undefined;
    const newLead = {
      ...createLifecycleLead(normalizedLead),
      leadPayload: sourcePayload,
      leadProfile,
    };
    attemptNumber = 1; // First attempt for a new lead
    lifecycleLead = startRouting(newLead);
    // Capture routing context for new leads
    lifecycleLead = { 
      ...lifecycleLead, 
      routingContext: captureRoutingContext() 
    };
  }

  if (debug) {
    console.log('[Orchestrator] Lead status:', previousStatus, '→', lifecycleLead.status);
    if (lifecycleLead.routingContext) {
      console.log('[Orchestrator] Routing context captured:', lifecycleLead.routingContext);
    }
  }

  if (sourcePayload && !lifecycleLead.leadPayload) {
    lifecycleLead = {
      ...lifecycleLead,
      leadPayload: sourcePayload,
      leadProfile: normalizeLeadProfile(sourcePayload, normalizedLead.leadTypeId),
    };
  }

  // --- Step 3: Run Agent Pool Selection ---
  const {
    agents,
    weights,
    targetPoolSize = 8,
    usePoolSelection = true,
    agentGuardrails,
    contractPressures,
  } = context;

  // --- DEV ONLY: Apply agent overrides ---
  // Priority: 1) Persisted context from lead, 2) Current session overrides
  let processedAgents: MockAgent[] | AgentWithPacing[] = agents;
  let devOverridesAppliedThisAttempt = false;
  
  // Create a set of agent IDs that should ignore caps/pricing (for eligibility context)
  const agentsIgnoringCaps = new Set<string>();
  const agentsIgnoringPricing = new Set<string>();
  
  if (source !== 'simulation') {
    if (usePersistedDevContext && existingLead?.routingContext) {
      // Apply overrides from persisted context (for retries)
      const ctx = existingLead.routingContext;
      processedAgents = (agents as MockAgent[]).map(agent => {
        const overriddenAgent = { ...agent };
        
        // Global DEV overrides
        if (ctx.forceWalletBalance !== undefined) {
          overriddenAgent.walletBalance = ctx.forceWalletBalance;
        }
        if (ctx.forceLicenses && ctx.forceLicenses.length > 0) {
          overriddenAgent.licensedStates = ctx.forceLicenses;
        }
        if (ctx.forceAllAgentsEligible && overriddenAgent.leadPreferences) {
          overriddenAgent.leadPreferences = overriddenAgent.leadPreferences.map(pref => ({
            ...pref,
            isActive: true,
          }));
        }
        
        // Per-agent overrides from persisted context
        if (ctx.perAgentOverrides?.enabled && ctx.perAgentOverrides.agentOverrides[agent.id]) {
          const perAgentCtx = ctx.perAgentOverrides.agentOverrides[agent.id];
          
          if (perAgentCtx.forceAcceptAllLeadTypes && overriddenAgent.leadPreferences) {
            overriddenAgent.leadPreferences = overriddenAgent.leadPreferences.map(pref => ({
              ...pref,
              isActive: true,
              bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
            }));
          } else if (perAgentCtx.overrideAcceptedLeadTypes.length > 0 && overriddenAgent.leadPreferences) {
            overriddenAgent.leadPreferences = overriddenAgent.leadPreferences.map(pref => ({
              ...pref,
              isActive: pref.isActive || perAgentCtx.overrideAcceptedLeadTypes.includes(pref.leadTypeId),
              bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
            }));
          }
          
          if (perAgentCtx.overrideLicensedStates.length > 0) {
            const existingStates = new Set(overriddenAgent.licensedStates);
            for (const state of perAgentCtx.overrideLicensedStates) {
              existingStates.add(state);
            }
            overriddenAgent.licensedStates = Array.from(existingStates);
          }
          
          if (perAgentCtx.ignoreContractCaps) {
            agentsIgnoringCaps.add(agent.id);
          }
          if (perAgentCtx.ignorePricingRules) {
            agentsIgnoringPricing.add(agent.id);
          }
        }
        
        return overriddenAgent;
      });
      devOverridesAppliedThisAttempt = true;
      console.log('[DEV Overrides] Applied persisted context overrides for retry');
    } else {
      // Apply current session overrides
      if (currentDevOverridesActive) {
        processedAgents = applyDevOverridesToPool(agents as MockAgent[]);
        devOverridesAppliedThisAttempt = true;
      }
      
      // Apply per-agent overrides from current session
      if (currentPerAgentOverridesActive) {
        processedAgents = applyPerAgentOverridesToPool(processedAgents as MockAgent[]);
        devOverridesAppliedThisAttempt = true;
        
        // Capture per-agent caps/pricing ignore flags from current session
        const snapshot = capturePerAgentOverridesSnapshot();
        if (snapshot) {
          for (const [agentId, overrides] of Object.entries(snapshot.agentOverrides)) {
            if (overrides.ignoreContractCaps) {
              agentsIgnoringCaps.add(agentId);
            }
            if (overrides.ignorePricingRules) {
              agentsIgnoringPricing.add(agentId);
            }
          }
        }
      }
    }
  }

  let poolAgents: MockAgent[] | AgentWithPacing[];
  let poolMeta: PoolMeta | undefined;
  let pacingScores = new Map<string, AgentPacingScore>();

  if (usePoolSelection && processedAgents.length > 10) {
    const poolResult = selectAgentPool({
      lead: {
        id: normalizedLead.id,
        leadTypeId: normalizedLead.leadTypeId,
        state: normalizedLead.state,
        vertical: normalizedLead.vertical,
      },
      allAgents: processedAgents as AgentWithPacing[],
      targetPoolSize,
      strategy: 'GOAL_AWARE_PACING',
      agentGuardrails,
      getPricingRule: context.getPricingRule,
      contractPressures,
    });
    
    poolAgents = poolResult.poolAgents;
    poolMeta = {
      ...poolResult.poolMeta,
      poolAgentIds: poolResult.poolAgents.map(a => a.id),
    };

    // Extract pacing scores from pool meta
    for (const score of poolResult.poolMeta.pacingScores) {
      pacingScores.set(score.agentId, score);
    }

    // Add contract pressure metadata if available
    if (contractPressures && contractPressures.size > 0) {
      let totalCommitted = 0;
      let totalDelivered = 0;
      let totalUrgency = 0;
      let agentsWithContracts = 0;
      let agentsUnderPressure = 0;
      let atRiskContracts = 0;

      for (const agent of poolResult.poolAgents) {
        const pressure = contractPressures.get(agent.id);
        if (pressure && pressure.activeContracts > 0) {
          agentsWithContracts++;
          totalCommitted += pressure.totalCommitted;
          totalDelivered += pressure.totalDelivered;
          totalUrgency += pressure.contractPressureScore;
          atRiskContracts += pressure.atRiskContracts;
          if (pressure.contractPressureScore > 0.3) {
            agentsUnderPressure++;
          }
        }
      }

      const averageUrgencyScore = agentsWithContracts > 0
        ? Math.round((totalUrgency / agentsWithContracts) * 1000) / 1000
        : 0;

      poolMeta.contractPressure = {
        agentsWithContracts,
        agentsUnderPressure,
        totalCommitted,
        totalDelivered,
        averageUrgencyScore,
        atRiskContracts,
        contractPressureApplied: agentsUnderPressure > 0,
      };
    }

    if (debug) {
      console.log('[Orchestrator] Pool selection:', poolMeta.strategy);
      console.log('[Orchestrator] Pool size:', poolMeta.poolSize);
    }
  } else {
    // Use all agents (small pool) - still apply DEV overrides
    poolAgents = processedAgents;
  }

  // --- Step 3.5: DEV Eligibility Injection (BEFORE real eligibility filtering) ---
  // This allows DEV agents to bypass eligibility checks for testing
  // Supports both current session state AND persisted context (for deterministic retries)
  let injectionMetadata: InjectionMetadata = {
    eligibilitySource: 'REAL',
    devInjectedAgentIds: [],
  };
  
  if (source !== 'simulation') {
    // Determine if we should use persisted context for injection
    const shouldUsePersistedContext = source === 'retry' && 
      existingLead?.routingContext?.devOverridesEnabled;
    
    if (shouldUsePersistedContext && existingLead?.routingContext) {
      // Use persisted context for deterministic retry behavior
      const injectionResult = resolveEligibleAgentsWithDevInjection(
        normalizedLead,
        poolAgents as MockAgent[],
        {
          usePersistedContext: true,
          persistedContext: existingLead.routingContext,
        }
      );
      
      poolAgents = injectionResult.agents;
      injectionMetadata = injectionResult.metadata;
      
      if (injectionMetadata.eligibilitySource === 'DEV_INJECTED') {
        devOverridesAppliedThisAttempt = true;
        
        if (debug) {
          console.log('[Orchestrator] DEV eligibility injection from PERSISTED context:', {
            injectedCount: injectionMetadata.devInjectedAgentIds.length,
            injectedAgentIds: injectionMetadata.devInjectedAgentIds,
          });
        }
      }
    } else if (isDevInjectionAvailable()) {
      // Use current session state for injection
      const injectionResult = resolveEligibleAgentsWithDevInjection(
        normalizedLead,
        poolAgents as MockAgent[]
      );
      
      poolAgents = injectionResult.agents;
      injectionMetadata = injectionResult.metadata;
      
      if (injectionMetadata.eligibilitySource === 'DEV_INJECTED') {
        devOverridesAppliedThisAttempt = true;
        
        if (debug) {
          console.log('[Orchestrator] DEV eligibility injection from SESSION state:', {
            injectedCount: injectionMetadata.devInjectedAgentIds.length,
            injectedAgentIds: injectionMetadata.devInjectedAgentIds,
          });
        }
      }
    }
  }

  // --- Step 4: Run Auction / Allocation ---
  const eligibilityContext = buildEligibilityContext(context, pacingScores, agentsIgnoringCaps, agentsIgnoringPricing);
  const poolSeed = `${normalizedLead.id}|${attemptNumber}|${[...poolAgents]
    .map(a => a.id)
    .sort()
    .join(',')}`;
  const random = createDeterministicRandom(poolSeed);

  let routingResult = routeLeadMultiAgent(
    normalizedLead,
    poolAgents as MockAgent[],
    weights,
    eligibilityContext,
    random
  );

  // Fallback: if pool selection yields zero eligible agents, retry with full pool
  const shouldFallback =
    usePoolSelection &&
    poolAgents.length > 0 &&
    routingResult.eligibleAgents.length === 0 &&
    processedAgents.length > poolAgents.length;

  if (shouldFallback) {
    poolAgents = processedAgents;
    const fallbackSeed = `${normalizedLead.id}|${attemptNumber}|${[...poolAgents]
      .map(a => a.id)
      .sort()
      .join(',')}`;
    const fallbackRandom = createDeterministicRandom(fallbackSeed);
    routingResult = routeLeadMultiAgent(
      normalizedLead,
      poolAgents as MockAgent[],
      weights,
      eligibilityContext,
      fallbackRandom
    );

    if (poolMeta) {
      poolMeta = {
        ...poolMeta,
        poolSize: poolAgents.length,
        poolAgentIds: poolAgents.map(a => a.id),
      };
    }
  }

  if (debug) {
    console.log('[Orchestrator] Routing outcome:', routingResult.outcome);
  }

  // --- Step 5 & 6: Attempt Delivery / Retry Decision ---
  let finalLead: LifecycleLead;
  let outcome: RoutingOutcome;
  let deliveryImpact: DeliveryImpact;
  let retryDecision: RoutingObservability['retryDecision'];

  if (routingResult.outcome === 'success') {
    const selectedContractId = routingResult.selectedAgentId
      ? contractPressures?.get(routingResult.selectedAgentId)?.highestUrgencyContract?.id
      : undefined;
    // Mark as routed
    finalLead = markRouted(
      lifecycleLead,
      routingResult.selectedAgentId,
      routingResult.selectedAgentName,
      routingResult.chargedAmount,
      selectedContractId
    );
    outcome = 'ROUTED';
    deliveryImpact = {
      agentId: routingResult.selectedAgentId,
      agentName: routingResult.selectedAgentName,
      chargedAmount: routingResult.chargedAmount,
      contractId: selectedContractId,
      willCountTowardTarget: true,
    };

    if (debug) {
      console.log('[Orchestrator] Routed to:', routingResult.selectedAgentName);
    }
  } else {
    // Mark as unsold first
    const unsoldLead = markUnsold(lifecycleLead);

    // --- DEV MODE: Unlimited retries to prevent auto-stocking ---
    // When DEV injection/overrides are active, we never auto-stock
    // This allows testing retry flows without hitting the retry limit
    const isDevModeActive = devOverridesAppliedThisAttempt || 
      injectionMetadata.eligibilitySource === 'DEV_INJECTED' ||
      lifecycleLead.routingContext?.devOverridesEnabled;
    
    // In DEV mode, effectively unlimited retries (bump maxRetries if needed)
    let leadForRetry = unsoldLead;
    if (isDevModeActive && unsoldLead.retryCount >= unsoldLead.maxRetries) {
      // Bump maxRetries to allow more retries in DEV mode
      leadForRetry = {
        ...unsoldLead,
        maxRetries: 9999, // Effectively unlimited
      };
      console.log('[DEV Mode] Extended maxRetries to allow unlimited retries');
    }

    // Determine retry eligibility (always true in DEV mode now)
    const canRetry = leadForRetry.retryCount < leadForRetry.maxRetries;

    if (canRetry) {
      // Queue for retry
      finalLead = queueRetry(leadForRetry, getRetryDelay(leadForRetry.retryCount ?? 0));
      outcome = 'RETRY_QUEUED';
      retryDecision = {
        willRetry: true,
        retryCount: finalLead.retryCount,
        maxRetries: isDevModeActive ? unsoldLead.maxRetries : finalLead.maxRetries, // Show original for display
        nextRetryAt: finalLead.nextRetryAt,
        reason: isDevModeActive 
          ? `DEV Mode: Retry ${finalLead.retryCount} scheduled (no limit)`
          : `Retry ${finalLead.retryCount}/${finalLead.maxRetries} scheduled`,
      };

      if (debug) {
        console.log('[Orchestrator] Queued for retry:', retryDecision);
      }
    } else {
      // Stock the lead (retries exhausted) - should not happen in DEV mode now
      finalLead = stockLead(unsoldLead);
      outcome = 'STOCKED';
      retryDecision = {
        willRetry: false,
        retryCount: finalLead.retryCount,
        maxRetries: finalLead.maxRetries,
        reason: 'All retries exhausted, lead stocked',
      };

      if (debug) {
        console.log('[Orchestrator] Lead stocked (retries exhausted)');
      }
    }

    deliveryImpact = { willCountTowardTarget: false };
  }

  // --- Step 6.5: Update routingContext with injection metadata ---
  // This ensures retries see the eligibility source that was used
  if (injectionMetadata.eligibilitySource === 'DEV_INJECTED') {
    const existingContext = finalLead.routingContext;
    finalLead = {
      ...finalLead,
      routingContext: {
        ...existingContext,
        devOverridesEnabled: true,
        eligibilitySource: injectionMetadata.eligibilitySource,
        devInjectedAgentIds: injectionMetadata.devInjectedAgentIds,
        capturedAt: existingContext?.capturedAt ?? Date.now(),
        source: existingContext?.source ?? (source === 'retry' ? 'retry' : 'lab'),
      },
    };
  }

  // --- Step 7: Build Observability Data ---
  const completedAt = Date.now();

  // Build evaluated agents list
  const evaluatedAgents: RoutingObservability['evaluatedAgents'] = [
    ...routingResult.eligibleAgents.map(ea => ({
      agentId: ea.agent.id,
      agentName: ea.agent.name,
      eligible: true as const,
      effectiveBid: ea.effectiveBid,
      priorityScore: ea.breakdown.finalScore,
      breakdown: ea.breakdown,
    })),
    ...routingResult.ineligibleAgents.map(ia => ({
      agentId: ia.agent.id,
      agentName: ia.agent.name,
      eligible: false as const,
      rejectionCode: ia.rejectionCode,
      rejectionReason: ia.rejectionReason,
    })),
  ];

  const observability: RoutingObservability = {
    startedAt,
    completedAt,
    durationMs: completedAt - startedAt,
    poolMeta,
    poolSize: poolAgents.length,
    eligibleCount: routingResult.eligibleAgents.length,
    ineligibleCount: routingResult.ineligibleAgents.length,
    totalAgentsEvaluated: routingResult.totalAgentsEvaluated,
    priorityScore: routingResult.outcome === 'success' ? routingResult.priorityScore : undefined,
    allocationBreakdown: routingResult.outcome === 'success' ? routingResult.breakdown : undefined,
    pickAttempts: routingResult.pickAttempts,
    evaluatedAgents,
    guardrailSummary: routingResult.guardrailSummary,
    guardrailsApplied: routingResult.guardrailSummary?.guardrailsApplied ?? false,
    pressureBoostApplied: routingResult.outcome === 'success' 
      ? routingResult.pressureBoostApplied ?? false 
      : false,
    pressureBoostValue: routingResult.outcome === 'success' 
      ? routingResult.pressureBoostValue 
      : undefined,
    eligibilitySource: injectionMetadata.eligibilitySource,
    devInjectedAgentIds: injectionMetadata.devInjectedAgentIds.length > 0 
      ? injectionMetadata.devInjectedAgentIds 
      : undefined,
    retryDecision,
    rejectionReason: routingResult.outcome === 'unsold' ? routingResult.reason : undefined,
    rejectionCode: routingResult.outcome === 'unsold' ? routingResult.code : undefined,
  };

  // --- Step 8: Build Final Result ---
  let persistencePromise: Promise<void> | undefined;
  
  const routingResultFinal: RoutingResult = {
    outcome,
    success: outcome === 'ROUTED',
    lead: finalLead,
    previousStatus,
    deliveryImpact,
    observability,
    isDryRun: dryRun,
    // persistence will be set below if applicable
  };

  // --- Step 9: Record Routing Event (append-only) ---
  // Recording happens AFTER routing is resolved, at the boundary
  let routingEventId: string | undefined;
  
  if (!dryRun) {
    const eventSource: RoutingEventSource = source === 'simulation' ? 'SIMULATION' : 'LIVE';
    const event = buildRoutingEvent(routingResultFinal, eventSource, {
      simulationRunId: input.simulationRunId,
      virtualTime: input.virtualTime,
    });
    recordRoutingEvent(event);
    routingEventId = event.id;
    
    // --- Step 10 & 11: Persist Lead and Routing Attempt to Database ---
    // Lead MUST be persisted before routing attempts (FK constraint)
    // Only persist for live/retry routing (skip simulation)
    if (source !== 'simulation') {
      // Create persistence promise that can be awaited by admin flows
      persistencePromise = upsertLeadToDb(finalLead)
        .then(() => {
          // Build and record routing attempt after lead is persisted
          try {
            const attemptData = buildRoutingAttempt(routingResultFinal, {
              source,
              attemptNumber,
              routingEventId,
              simulationRunId: input.simulationRunId,
              devOverridesApplied: finalLead.routingContext?.devOverridesEnabled ?? devOverridesAppliedThisAttempt,
              eligibilitySource: injectionMetadata.eligibilitySource,
              devInjectedAgentIds: injectionMetadata.devInjectedAgentIds,
              agentAttributions: injectionMetadata.agentAttributions,
            });
            
            return recordRoutingAttemptToDb(attemptData);
          } catch (err) {
            console.error('[Orchestrator] Failed to build routing attempt:', err);
            throw err;
          }
        })
        .then(() => {
          // Resolve with void when complete
          return;
        })
        .catch(err => {
          console.error('[Orchestrator] Failed to persist lead or routing attempt:', err);
          // Re-throw so callers can handle
          throw err;
        });
      
      // Attach to result for admin flows
      routingResultFinal.persistence = persistencePromise;
    }
  }

  return routingResultFinal;
}

// --- Convenience Functions ---

/**
 * Route a new lead (shorthand for simulation/testing)
 */
export function routeNewLead(
  lead: IncomingLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  options: {
    getPricingRule?: RoutingContext['getPricingRule'];
    checkIntake?: RoutingContext['checkIntake'];
    agentGuardrails?: Map<string, GuardrailInputs>;
    contractPressures?: Map<string, AgentContractPressure>;
    targetPoolSize?: number;
    debug?: boolean;
  } = {}
): RoutingResult {
  return routeIncomingLead(
    {
      lead,
      source: 'simulation',
      debug: options.debug,
    },
    {
      agents,
      weights,
      getPricingRule: options.getPricingRule,
      checkIntake: options.checkIntake,
      agentGuardrails: options.agentGuardrails,
      contractPressures: options.contractPressures,
      targetPoolSize: options.targetPoolSize,
      usePoolSelection: agents.length > 10,
    }
  );
}

/**
 * Route a retry lead (from retry worker or admin actions)
 * 
 * IMPORTANT: This is the ONLY valid entry point for retry routing.
 * DO NOT call routeIncomingLead() directly for retries.
 * 
 * This function ensures:
 * 1. Old routingContext is cleared (forces fresh evaluation)
 * 2. DEV overrides are re-evaluated from CURRENT session state
 * 3. Full normalization pipeline runs from scratch
 * 4. No eligibility inheritance from prior attempts
 * 
 * CRITICAL: Retry routing re-runs the FULL pipeline from scratch:
 * - Re-normalizes lead data using current canonical rules
 * - Re-evaluates DEV overrides using CURRENT session state (not persisted)
 * - Evaluates eligibility from zero - no inheritance from prior attempts
 * 
 * The only differences from LIVE routing are:
 * - Attempt count is incremented
 * - Audit trail shows 'retry' source
 * - Retry reason metadata is captured
 */
export function routeRetryLead(
  existingLead: LifecycleLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  options: {
    getPricingRule?: RoutingContext['getPricingRule'];
    checkIntake?: RoutingContext['checkIntake'];
    agentGuardrails?: Map<string, GuardrailInputs>;
    contractPressures?: Map<string, AgentContractPressure>;
    targetPoolSize?: number;
    debug?: boolean;
  } = {}
): RoutingResult {
  const debug = options.debug ?? false;
  
  if (debug) {
    console.log('[RetryRouting] Starting retry for lead:', existingLead.id);
    console.log('[RetryRouting] Previous attempt count:', existingLead.retryCount);
    console.log('[RetryRouting] Previous status:', existingLead.status);
  }
  
  // CRITICAL: Clear persisted routing context to force fresh DEV override evaluation
  // This ensures DEV overrides activated AFTER the initial failure are respected
  const leadForRetry: LifecycleLead = {
    ...existingLead,
    // Clear old routing context - it will be re-captured with current session state
    routingContext: undefined,
  };
  
  if (debug) {
    console.log('[RetryRouting] Cleared persisted routing context - will re-evaluate from current session');
    console.log('[RetryRouting] Current DEV overrides active:', areDevOverridesActive());
    console.log('[RetryRouting] Current per-agent overrides active:', arePerAgentOverridesActive());
  }
  
  // Route through the main orchestrator with fresh context evaluation
  // The orchestrator will:
  // 1. Re-validate and normalize the lead data
  // 2. Capture fresh DEV overrides from current session state
  // 3. Evaluate all eligibility dimensions from scratch
  return routeIncomingLead(
    {
      lead: {
        id: existingLead.id,
        leadTypeId: existingLead.leadTypeId,
        leadTypeName: existingLead.leadTypeName,
        vertical: existingLead.vertical,
        state: existingLead.state,
        estimatedValue: existingLead.estimatedValue,
      },
      source: 'retry',
      existingLead: leadForRetry,
      debug,
    },
    {
      agents,
      weights,
      getPricingRule: options.getPricingRule,
      checkIntake: options.checkIntake,
      agentGuardrails: options.agentGuardrails,
      contractPressures: options.contractPressures,
      targetPoolSize: options.targetPoolSize,
      usePoolSelection: agents.length > 10,
    }
  );
}

// --- Log Data Builder (for database persistence) ---

export interface RoutingLogData {
  leadId: string;
  leadTypeId: string;
  leadTypeName: string;
  leadState: string;
  result: 'success' | 'unsold';
  rejectionReason?: string;
  rejectionCode?: string;
  chargedAmount?: number;
  priorityScore?: number;
  allocationBreakdown?: AllocationBreakdown;
  selectedAgentId?: string;
  selectedAgentName?: string;
  evaluatedAgents: RoutingObservability['evaluatedAgents'];
  pickAttempts: AgentPickAttempt[];
  totalAgentsEvaluated: number;
  eligibleCount: number;
  ineligibleCount: number;
  leadStatusBefore?: string;
  leadStatusAfter: string;
  retryCount: number;
  poolMeta?: PoolMeta;
}

/**
 * Build log data from routing result for database persistence
 */
export function buildRoutingLogData(result: RoutingResult): RoutingLogData {
  return {
    leadId: result.lead.id,
    leadTypeId: result.lead.leadTypeId,
    leadTypeName: result.lead.leadTypeName,
    leadState: result.lead.state,
    result: result.success ? 'success' : 'unsold',
    rejectionReason: result.observability.rejectionReason,
    rejectionCode: result.observability.rejectionCode,
    chargedAmount: result.deliveryImpact.chargedAmount,
    priorityScore: result.observability.priorityScore,
    allocationBreakdown: result.observability.allocationBreakdown,
    selectedAgentId: result.deliveryImpact.agentId,
    selectedAgentName: result.deliveryImpact.agentName,
    evaluatedAgents: result.observability.evaluatedAgents,
    pickAttempts: result.observability.pickAttempts,
    totalAgentsEvaluated: result.observability.totalAgentsEvaluated,
    eligibleCount: result.observability.eligibleCount,
    ineligibleCount: result.observability.ineligibleCount,
    leadStatusBefore: result.previousStatus,
    leadStatusAfter: result.lead.status,
    retryCount: result.lead.retryCount,
    poolMeta: result.observability.poolMeta,
  };
}
